import { Directive, effect, ElementRef, inject, input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { TooltipDirective } from './tooltip/tooltip.directive';

@Directive({
  selector: '[sharedCoreTooltipOnContentOverflow]',
  hostDirectives: [{ directive: TooltipDirective, inputs: ['dataTestId'] }],
})
export class TooltipOnContentOverflowDirective implements OnInit {
  private readonly elementRef = inject(ElementRef);
  private readonly tooltipDirective = inject(TooltipDirective);

  readonly displayedValue = input.required<string>();

  /**
   * If provided, the directive will reevaluate the need for a tooltip whenever the provided subject emits a value.
   */
  readonly reevaluationSubject = input<Subject<boolean>>();

  constructor() {
    effect(() => {
      this.evaluateNeedOfTooltip();
    });
  }

  ngOnInit() {
    this.reevaluationSubject()?.subscribe((bool) => {
      if (!bool) return;

      this.evaluateNeedOfTooltip();
    });
  }

  evaluateNeedOfTooltip() {
    const element = this.elementRef.nativeElement;

    // To avoid having to provide `displayedValue` both as an input and in the template,
    // this directive sets the `innerText` of the host element automatically.
    // However, in some cases the directive might be used on a container element
    // that contains more than just plain text (e.g. additional icons or elements).
    // In such cases, the text content must be explicitly set in the template instead.
    if (!element.innerText) {
      element.innerText = this.displayedValue();
    }

    const overflow = element.scrollWidth > element.offsetWidth;

    if (!overflow) {
      this.tooltipDirective.tooltip.set('');
      element.classList.remove('truncate');
      return;
    }

    this.tooltipDirective.tooltip.set(this.displayedValue());
    element.classList.add('truncate');
  }
}
